import { Tooltip } from '@mantine/core';
import classes from './ButtonToggle.module.css';

export interface ButtonToggleOption<T extends string = string> {
  value: T;
  label?: string;
  icon?: React.ComponentType<{ size?: number | string }>;
  /** The accessible name of an icon-only option, also shown as a tooltip. */
  ariaLabel?: string;
  badge?: number | string;
  /** Show a green indicator dot next to the label (e.g., "configured" state) */
  indicator?: boolean;
  /** Show a status badge label (e.g., "SCHEDULED") next to the label */
  statusBadge?: string;
  disabled?: boolean;
  disabledTooltip?: string;
}

export interface ButtonToggleProps<T extends string = string> {
  options: ButtonToggleOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
}

export function ButtonToggle<T extends string = string>({
  options,
  value,
  onChange,
  size = 'md',
}: ButtonToggleProps<T>) {
  return (
    <div className={classes.toggle}>
      {options.map((opt) => {
        const isActive = opt.value === value;
        const isDisabled = opt.disabled === true;
        const Icon = opt.icon;
        const btnClasses = [
          classes.btn,
          isActive ? classes.active : '',
          isDisabled ? classes.disabled : '',
          size === 'sm' ? classes.sm : '',
        ]
          .filter(Boolean)
          .join(' ');

        const btn = (
          <button
            key={opt.value}
            type="button"
            className={btnClasses}
            onClick={() => !isDisabled && onChange(opt.value)}
            disabled={isDisabled}
            aria-pressed={isActive}
            aria-label={opt.ariaLabel}
            title={opt.ariaLabel}
          >
            {Icon && <Icon size={14} />}
            {opt.label && <span>{opt.label}</span>}
            {opt.indicator && (
              <span className={classes.indicator} />
            )}
            {opt.statusBadge && (
              <span className={classes.statusBadge}>{opt.statusBadge}</span>
            )}
            {opt.badge != null && (
              <span className={classes.badge}>{opt.badge}</span>
            )}
          </button>
        );

        if (isDisabled && opt.disabledTooltip) {
          return (
            <Tooltip key={opt.value} label={opt.disabledTooltip} withArrow>
              <span style={{ cursor: 'not-allowed' }}>{btn}</span>
            </Tooltip>
          );
        }

        return btn;
      })}
    </div>
  );
}

export default ButtonToggle;
